import { Component, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { interval, Subscription } from 'rxjs';
import { DataSourceService } from '../../../service/datatsource.service';
import { NotificationService } from '@nrwl/common-lib';
import { ViewResultsDialogComponent } from '../view-results-dialog/view-results-dialog.component';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'nrwl-bulk-renaming-dialog',
  templateUrl: './bulk-renaming-dialog.component.html',
  styleUrls: ['./bulk-renaming-dialog.component.scss']
})
export class BulkRenamingDialogComponent implements OnDestroy {
  applyingFileName: string;
  validationInProgress = true;
  isRenamingJobCompleted = false;
  renamingInProgress = false;
  renamingCompletedPercent = 0;
  renamingSummary = [];
  renamingJobProgressMsg: string;
  activeJobId: string | null = null;
  getJmepRenamingStatus: Subscription | null = null;
  renamingJobStatus: string | null = null;
  errorOnValidationProcess: string | null = null;
  progresStatusMessage = {
    VALIDATION_IN_PROGRESS: 'Config file validation in progress...',
    APPLY_IN_PROGRESS: 'Config file apply in progress...',
    APPLY_FAILED: 'Config file apply job failed...',
    FINISHED: 'Config file applied successfully...',
    VALIDATION_FAILED: 'Config file validation failed'
  };
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private dialogRef: MatDialogRef<BulkRenamingDialogComponent>,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dataSourceService: DataSourceService,
    private notificationService: NotificationService,
    private dialog: MatDialog
  ) {
    this.applyingFileName = data.fileName;
    this.validateRenamingFile();
  }

  // Validating renaming file
  validateRenamingFile(): void {
    this.dataSourceService
      .validateJmepBulkRenamingFile(this.applyingFileName)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.errorOnValidationProcess = null;
          if (response.status === 'success' && response.data?.details) {
            this.getRenamingFileValidationSummary(response.data.details);
            this.validationInProgress = false;
          } else {
            this.notificationService.onFail('Bulk Renaming is not applied');
            this.validationInProgress = false;
            this.dialogRef.close();
          }
        },
        error => {
          this.errorOnValidationProcess = error.error.data.message;
          this.validationInProgress = false;
        }
      );
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  // Process and display validation summary
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getRenamingFileValidationSummary(fileInfo: any): void {
    this.renamingSummary = [
      { summaryTxt: `${fileInfo.totalRecords} entries in file` },
      { summaryTxt: `${fileInfo.invalidConfigCount} invalid entries` },
      { summaryTxt: `${fileInfo.validConfigCount} devices have changes identified` },
      { summaryTxt: `${fileInfo.offlineDeviceCount} devices currently offline` },
      { summaryTxt: `${fileInfo.missingDeviceCount} devices not in inventory` },
      { summaryTxt: `${fileInfo.skippedConfigCount} devices skipped` }
    ];
  }

  // Proceed with renaming
  proceedBulkRenamingAction(): void {
    this.renamingJobProgressMsg = 'Config file validation in progress...';
    this.renamingInProgress = true;
    this.validationInProgress = true;
    this.dataSourceService
      .applyJmepBulkRenamingFile(this.applyingFileName)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(res => {
        if (res.status === 'success') {
          this.validationInProgress = false;
          this.activeJobId = res?.data?.details?.jobId;
          this.getBulkRenamingFileJobStatus();
        } else {
          this.notificationService.onFail(res?.data?.message);
          this.renamingInProgress = false;
          this.validationInProgress = false;
          this.dialogRef.close();
        }
      });
  }

  // Fetch renaming job status periodically
  getBulkRenamingFileJobStatus(): void {
    this.validationInProgress = true;
    if (this.activeJobId) {
      this.dataSourceService
        .getBulkRenamingFileJobStatus(this.activeJobId)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(response => {
          if (response.status === 'success') {
            this.validationInProgress = false;
            this.renamingJobProgressMsg = this.getApplyJobStatusprogressMsg(
              response.data.details.status
            );
            this.renamingCompletedPercent = response.data.details.percentageCompleted;
            if (this.renamingCompletedPercent < 100) {
              this.getJmepRenamingStatus = interval(1000).subscribe(() =>
                this.getBulkRenamingFileJobStatus()
              );
            } else {
              this.validationInProgress = false;
              this.getJmepRenamingStatus?.unsubscribe();
              this.dataSourceService
                .getFieldMappingFileSummary(this.applyingFileName)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe(res => this.updateRenamingFileApplySummary(res));
            }
          } else {
            this.validationInProgress = false;
            this.renamingJobStatus = 'Failed: ' + response.data.message;
            this.renamingInProgress = false;
          }
        });
    } else {
      this.validationInProgress = false;
    }
  }

  // Update renaming file apply summary and mark completion
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  updateRenamingFileApplySummary(res: any): void {
    if (res.status === 'success') {
      this.renamingSummary = this.processRenamingSummary(res.data.details.previousJobResultSummary);
      this.renamingInProgress = false;
      this.isRenamingJobCompleted = true;
      this.renamingJobStatus = `Renaming completed for ${this.applyingFileName}`;
    } else {
      this.notificationService.onFail(res.data.message);
      this.dialogRef.close();
    }
  }

  // Helper to process summary results
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  processRenamingSummary(summary: any): any {
    const failedCount = summary.invalidConfigCount + summary.offlineDeviceCount;
    const skippedCount = summary.skippedConfigCount + summary.missingDeviceCount;
    return [
      { summaryTxt: `${summary.validConfigCount} devices updated successfully` },
      { summaryTxt: `${skippedCount} devices skipped` },
      { summaryTxt: `${failedCount} devices failed` }
    ];
  }

  // Close the dialog
  closeDialog(): void {
    this.getJmepRenamingStatus?.unsubscribe();
    this.dialogRef.close();
  }

  // View renaming results
  viewRenamingResult(): void {
    this.dialogRef.close();
    this.dialog.open(ViewResultsDialogComponent, {
      width: '1300px',
      height: '600px',
      data: { fileNameData: this.applyingFileName }
    });
  }

  getApplyJobStatusprogressMsg(status: string): string {
    return this.progresStatusMessage[status];
  }
}
